import { Link } from 'ui/baseComponents/Link'
import { Text } from 'ui/baseComponents/Text'
import { colors } from 'theme/colors'

import activityIconAerobic from 'assets/img/activity-aerobic.svg'
import activityIconBalance from 'assets/img/activity-balance.svg'
import activityIconFlexibility from 'assets/img/activity-flexibility.svg'
import activityIconStrength from 'assets/img/activity-strength.svg'
import { VIDEO_LINKS } from 'ui/clinicianScreens/Patient/Report/consts'
import ReportTitle from 'ui/clinicianScreens/Patient/Report/components/ReportTitle'
import { useReportTranslation } from 'utilities/i18n'

export const TOP_ACTIVITY_COLORS = [
  colors.GREEN[500],
  colors.PINK[700],
  colors.BONDI[300],
  colors.ORANGE[400],
  colors.BLUEBERRY[400],
]

export const ACTIVITY_COLORS = {
  time: colors.PRIMARY[1000],
  highIntensityHR: colors.ORANGE[400],
  lowIntensityHR: colors.BLUEBERRY[400],
}

export const ACTIVITY_CATEGORIES_ENUMS = {
  Aerobic: 'activity.aerobic',
  Balance: 'activity.balance',
  Flexibility: 'activity.flexibility',
  Strength: 'activity.strength-training',
}

export const ACTIVITY_CATEGORY_I18N_KEYS = {
  'activity.aerobic': 'report.activity.categoryAerobicDisplay',
  'activity.strength-training': 'report.activity.categoryStrengthDisplay',
  'activity.flexibility': 'report.activity.categoryFlexibilityDisplay',
  'activity.balance': 'report.activity.categoryBalanceDisplay',
}

export const ACTIVITY_CATEGORY_ICONS_MAP = new Map([
  [ACTIVITY_CATEGORIES_ENUMS.Aerobic, activityIconAerobic],
  [ACTIVITY_CATEGORIES_ENUMS.Balance, activityIconBalance],
  [ACTIVITY_CATEGORIES_ENUMS.Flexibility, activityIconFlexibility],
  [ACTIVITY_CATEGORIES_ENUMS.Strength, activityIconStrength],
])

export const ACTIVITIES_TABLE_HEADINGS = {
  activity: 'Activity',
  total: 'Total Time',
  logs: 'Logs | Prev',
  rpeAvg: 'RPE Avg | Prev',
  highIntensityDuration: 'Time in zone 3+',
  category: 'Category',
}

export const ACTIVITY_CATEGORIES_TABLE_HEADINGS = {
  category: 'Category',
  total: 'Total Time',
}

export const ActivityTableHelperDefault = () => {
  const { t } = useReportTranslation()
  return (
    <Text
      variant="body14"
      component="p"
      data-cy="activity-table-helper-default"
    >
      {t('report.activity.helperDefault')}{' '}
      <Link href={VIDEO_LINKS.importanceOfActivity} external underline light>
        {t('report.common.watchOurVideoCap')}
      </Link>
      .
    </Text>
  )
}

export const ActivityTableHelperNoHr = () => {
  const { t } = useReportTranslation()
  return (
    <ReportTitle>
      <Text
        variant="body14"
        component="p"
        data-cy="activity-table-helper-no-hr"
      >
        <Text variant="body14B">{t('report.activity.noHrBold')}</Text>{' '}
        {t('report.activity.noHrBody')}{' '}
        <Link href={VIDEO_LINKS.hrZones} external underline light>
          {t('report.common.watchOurVideoCap')}
        </Link>{' '}
        {t('report.activity.noHrAfter')}
      </Text>
    </ReportTitle>
  )
}

export const ActivityTableHelperFewCategories = () => {
  const { t } = useReportTranslation()
  return (
    <ReportTitle>
      <Text
        variant="body14"
        component="p"
        data-cy="activity-table-helper-few-categories"
      >
        <Text variant="body14B">{t('report.activity.fewCategoriesBold')}</Text>{' '}
        {t('report.activity.fewCategoriesBody')}{' '}
        <Link href={VIDEO_LINKS.aerobic} external underline light>
          {t('report.activity.categoryAerobic')}
        </Link>
        ,{' '}
        <Link href={VIDEO_LINKS.strength} external underline light>
          {t('report.activity.categoryStrength')}
        </Link>
        ,{' '}
        <Link href={VIDEO_LINKS.flexibility} external underline light>
          {t('report.activity.categoryFlexibility')}
        </Link>
        ,{' '}
        <Link href={VIDEO_LINKS.balance} external underline light>
          {t('report.activity.categoryBalance')}
        </Link>
        .
      </Text>
    </ReportTitle>
  )
}

export const ActivityTableHelperNoRpe = () => {
  const { t } = useReportTranslation()
  return (
    <ReportTitle>
      <Text
        variant="body14"
        component="p"
        data-cy="activity-table-helper-no-rpe"
      >
        <Text variant="body14B">{t('report.activity.noRpeBold')}</Text>{' '}
        {t('report.activity.noRpeBody')}{' '}
        <Link href={VIDEO_LINKS.rpe} external underline light>
          {t('report.common.watchOurVideoCap')}
        </Link>{' '}
        {t('report.activity.noRpeAfter')}
      </Text>
    </ReportTitle>
  )
}

export const ActivityTimeInCategoryHelper = () => {
  const { t } = useReportTranslation()
  return (
    <Text
      variant="body14"
      component="p"
      data-cy="activity-helper-time-in-category"
    >
      {t('report.activity.timeInCategory')}
    </Text>
  )
}

export const HelperActivityLegacy = () => {
  const { t } = useReportTranslation()
  return (
    <Text variant="body14" component="p" data-cy="activity-helper">
      {t('report.activity.helperLegacyBody')}{' '}
      <Link href={VIDEO_LINKS.importanceOfActivity} external underline light>
        {t('report.common.watchOurVideoCap')}
      </Link>
      .
    </Text>
  )
}

export const HelperActivityEmptyState = () => {
  const { t } = useReportTranslation()
  return (
    <Text variant="body14" component="p" data-cy="activity-helper-no-data">
      {t('report.activity.helperEmptyStateIntro')}{' '}
      {t('report.activity.helperEmptyStateBody')}{' '}
      <Link href={VIDEO_LINKS.howToLogActivity} external underline light>
        {t('report.symptomLog.howToLog')}
      </Link>{' '}
      {t('report.symptomLog.orOnThe')}{' '}
      <Link href={VIDEO_LINKS.importanceOfActivity} external underline light>
        {t('report.activity.importanceOfActivity')}
      </Link>
      .
    </Text>
  )
}
